<script setup>
import {Head, Link} from '@inertiajs/vue3';
import GeneralLayout from "@/Layouts/GeneralLayout.vue";
import {computed, ref, inject} from "vue";
import {
    FwbAccordion,
    FwbAccordionContent,
    FwbAccordionHeader,
    FwbAccordionPanel,
} from 'flowbite-vue'
import GoogleAuthButton from "@/Components/Marketing/GoogleAuthButton.vue";
import {track} from "@/Composables/useAnalytics.js";


// Ziggy's route() via injection: the global `route` only exists in the browser, so calling it in
// script code breaks server-side rendering ("ReferenceError: route is not defined").
const route = inject('route');
const metaData = {
    canonical: 'https://mockready.com/',
    title: "AI Speaking Coach: Job Interview & English Communication Practice", // the app title template appends " | MockReady"
    description: "Practise job interviews, workplace conversations and IELTS speaking with an AI coach. Get a scored report with corrections in about 5 minutes. Start free.",
};

defineProps({
    canLogin: {
        type: Boolean,
    },
    canRegister: {
        type: Boolean,
    },
    is_authenticated: {
        type: Boolean,
    },
    laravelVersion: {
        type: String,
        required: true,
    },
    phpVersion: {
        type: String,
        required: true,
    },
    plans: {
        type: Array,
        required: true
    },
    isAuthenticated: {
        type: Boolean,
        default: false
    },
    config: {
        type: Object,
        required: true
    }

});

// Hero segments map 1:1 to practice tracks. Job interviews lead (see docs/growth/2026-09-positioning.md).
const segments = [
    {
        key: 'interview',
        tab: 'Job interviews',
        intro: 'Rehearse the questions interviewers actually ask, out loud, before the real thing.',
        bullets: [
            'Answer common HR questions: tell me about yourself, your strengths, why this role',
            'Hear how clear and structured your answers really sound',
            'Get corrections for grammar, vocabulary and pronunciation',
        ],
        examples: [
            'Tell me about yourself.',
            'What is your greatest strength?',
            'Describe a challenge you faced and how you handled it.',
            'Why do you want this role?',
        ],
        ctaLabel: 'Practise a job interview',
    },
    {
        key: 'workplace',
        tab: 'Work & everyday',
        intro: 'Get comfortable with the conversations that make you hesitate: meetings, calls and small talk.',
        bullets: [
            'Practise small talk, giving your opinion and explaining a process',
            'Learn to disagree politely and clearly',
            'Find the words and phrases that trip you up',
        ],
        examples: [
            'How was your weekend?',
            'What do you think about working from home?',
            'Can you explain how your team handles a new request?',
            'A colleague disagrees with your plan. What do you say?',
        ],
        ctaLabel: 'Practise work conversations',
    },
    {
        key: 'ielts',
        tab: 'IELTS Speaking',
        intro: 'Take an IELTS-style speaking test with an AI examiner and get an estimated band score.',
        bullets: [
            'Part 1 questions in 5 minutes, or a full 3-part mock test',
            'Estimated band on all four IELTS speaking criteria',
            'Your transcript, with mistakes corrected',
        ],
        examples: [
            'Do you work or are you a student?',
            'What do you like about your hometown?',
            'Describe a book you enjoyed reading. (Part 2)',
            'Why do people read less than they used to? (Part 3)',
        ],
        ctaLabel: 'Practise IELTS speaking',
    },
];

const userSegment = ref('interview');
const activeSegment = computed(() => segments.find(s => s.key === userSegment.value) || segments[0]);

const setUserSegment = (segment) => {
    if (segment === userSegment.value) return;
    userSegment.value = segment;
    track('cta_clicked', {location: 'home_hero_segment', label: segment});
};

const onHeroEmailClick = () => track('cta_clicked', {location: 'home_hero', label: 'sign_up_with_email', track: userSegment.value});
const onHeroStartClick = () => track('cta_clicked', {location: 'home_hero', label: `start_${userSegment.value}`});

const companies = [
    {name: 'Cambridge University', image: 'cambridge.jpg'},
    {name: 'British Council', image: 'british-council.svg'},
    {name: 'IELTS Official', image: 'ielts.svg'},
    {name: 'Deloitte', image: 'deloitte.svg'}
];

const faqItems = {
    'About MockReady': [
        {
            question: 'What is MockReady?',
            answer: 'MockReady is an AI speaking coach. Pick what you are preparing for (a job interview, work and everyday conversations, or the IELTS speaking test), answer questions out loud, and get a scored report on your fluency, vocabulary, grammar and pronunciation, with corrections.'
        },
        {
            question: 'How long does a session take?',
            answer: 'About 5 minutes. For IELTS you can also take a full mock test with all three parts, which takes about 30 minutes.'
        },
        {
            question: 'What do I need?',
            answer: 'A browser and a microphone. The AI reads each question aloud and records your answer. A camera is optional and is never used for scoring.'
        },
        {
            question: 'How is my speaking scored?',
            answer: 'Every session is scored on four criteria (fluency and coherence, vocabulary, grammar, pronunciation) on a 1–9 scale. Interview and workplace sessions show this as a speaking score with a plain-language level, such as 6 = Competent or 7 = Good. IELTS sessions show an estimated band score. Scores are AI estimates, not official results.'
        },
    ],
    'Job interviews & work': [
        {
            question: 'What interview questions will I practise?',
            answer: 'Common questions that come up in most interviews: tell me about yourself, your strengths and weaknesses, a challenge you handled, and why you want the role. Your report also looks at how clearly your answers are structured.'
        },
        {
            question: 'Can I practise for a specific role or company?',
            answer: 'Not yet. Sessions cover general interview questions that apply to most roles. Role-specific practice is something we are looking at.'
        },
        {
            question: 'What does Work & everyday practice cover?',
            answer: 'The conversations people often hesitate in: small talk, giving your opinion, explaining a process, and handling a disagreement politely.'
        },
    ],
    'IELTS Speaking': [
        {
            question: 'Is this the official IELTS speaking test?',
            answer: 'No. MockReady is not affiliated with IELTS. Sessions follow the IELTS speaking format, and your band score is an AI estimate against the four IELTS speaking criteria.'
        },
        {
            question: 'Can I take a full IELTS mock test?',
            answer: 'Yes. A full mock covers Parts 1, 2 and 3 and takes about 30 minutes. It uses one credit, like any other session.'
        },
        {
            question: 'Where can I find IELTS cue cards and Part 1 questions?',
            answer: 'Our free IELTS library has cue cards, Part 1 question topics and sample speaking tests. You will find the links in the IELTS section of this page and in the footer.'
        },
    ],
    'Plans & credits': [
        {
            question: 'Is it free?',
            answer: 'Your free account includes 5 AI speaking sessions with scored feedback. No card needed. After that, you can pick a plan to keep practising.'
        },
        {
            question: "What's a credit?",
            answer: 'One credit is one AI speaking session on any track. A credit is only used once you finish answering at least one part; if a session ends before that, nothing is charged.'
        },
        {
            question: 'Can I cancel?',
            answer: "Yes, anytime from Billing. Your plan stays active until the end of the period you've already paid for, and you won't be charged again."
        },
    ],
};

const activeTab = ref('aipeer')

const howItWorksTabs = [
    {
        name: 'aipeer',
        label: 'AI speaking coach',
        icon: '<path d="M10 0a10 10 0 1 0 10 10A10.011 10.011 0 0 0 10 0Zm0 5a3 3 0 1 1 0 6 3 3 0 0 1 0-6Zm0 13a8.949 8.949 0 0 1-4.951-1.488A3.987 3.987 0 0 1 9 13h2a3.987 3.987 0 0 1 3.951 3.512A8.949 8.949 0 0 1 10 18Z"/>'
    },
    {
        name: 'paired',
        label: 'Practice partner',
        icon: '<path d="M6.143 0H1.857A1.857 1.857 0 0 0 0 1.857v4.286C0 7.169.831 8 1.857 8h4.286A1.857 1.857 0 0 0 8 6.143V1.857A1.857 1.857 0 0 0 6.143 0Zm10 0h-4.286A1.857 1.857 0 0 0 10 1.857v4.286C10 7.169 10.831 8 11.857 8h4.286A1.857 1.857 0 0 0 18 6.143V1.857A1.857 1.857 0 0 0 16.143 0Zm-10 10H1.857A1.857 1.857 0 0 0 0 11.857v4.286C0 17.169.831 18 1.857 18h4.286A1.857 1.857 0 0 0 8 16.143v-4.286A1.857 1.857 0 0 0 6.143 10Zm10 0h-4.286A1.857 1.857 0 0 0 10 11.857v4.286c0 1.026.831 1.857 1.857 1.857h4.286A1.857 1.857 0 0 0 18 16.143v-4.286A1.857 1.857 0 0 0 16.143 10Z"/>'
    }
]

const formatPrice = (price) => {
    if (price === 0 || price === '0') return '0.00';
    const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
    return numericPrice.toFixed(2);
};

const getPeriodText = (interval, intervalCount) => {
    if (intervalCount === 1) return `per ${interval}`;
    if (interval === 'month') {
        if (intervalCount === 3) return 'per quarter';
        if (intervalCount === 6) return 'per 6 months';
        if (intervalCount === 12) return 'per year';
    }
    return `per ${intervalCount} ${interval}s`;
};

// TODO(owner): verify claims in this table ("2-4 weeks", "$9.99-29.97", "3x cheaper", "native speakers").
const comparisonFeatures = [
    {
        name: "Realistic rehearsal: interview, workplace and IELTS questions",
        mockready: "Yes ✓",
        mockreaddyNote: "Questions read aloud, answered out loud",
        traditional: {value: "Yes ✓", status: "positive"},
        tutors: {value: "Yes ✓", status: "positive"},
        apps: {value: "Partial ⚠️", status: "neutral"},
        highlight: true
    },
    {
        name: "Time to score improvement",
        mockready: "Fast (2-4 weeks) ✓",
        traditional: {value: "Slow (8-12 weeks) ✕", status: "negative"},
        tutors: {value: "Medium (4-8 weeks) ⚠️", status: "neutral"},
        apps: {value: "Slow (8-12 weeks) ✕", status: "negative"},
        highlight: true
    },
    {
        name: "Monthly cost",
        mockready: "$9.99-29.97 ✓",
        mockreaddyNote: "3x cheaper!",
        traditional: {value: "$200-500", status: "negative"},
        tutors: {value: "$150-300", status: "negative"},
        apps: {value: "$10-30", status: "neutral"},
        highlight: true
    },
    {
        name: "Practice with native speakers",
        mockready: "Yes ✓",
        traditional: {value: "Yes ✓", status: "positive"},
        tutors: {value: "Yes ✓", status: "positive"},
        apps: {value: "No ✕", status: "negative"},
        highlight: false
    },
    {
        name: "Scored report with corrections",
        mockready: "Yes ✓",
        mockreaddyNote: "Fluency, vocabulary, grammar, pronunciation",
        traditional: {value: "No ✕", status: "negative"},
        tutors: {value: "Limited ⚠️", status: "neutral"},
        apps: {value: "Basic ⚠️", status: "neutral"},
        highlight: true
    },
    {
        name: "Available practice time",
        mockready: "24/7 ✓",
        traditional: {value: "1-3 hours/week ✕", status: "negative"},
        tutors: {value: "1-2 hours/week ✕", status: "negative"},
        apps: {value: "Unlimited ✓", status: "positive"},
        highlight: false
    },
    {
        name: "Practice schedule flexibility",
        mockready: "Any time, any day ✓",
        traditional: {value: "Fixed schedule ✕", status: "negative"},
        tutors: {value: "Limited slots ✕", status: "negative"},
        apps: {value: "Any time ✓", status: "positive"},
        highlight: false
    },
    {
        name: "Progress analytics & tracking",
        mockready: "Comprehensive ✓",
        mockreaddyNote: "With improvement graphs",
        traditional: {value: "Basic ⚠️", status: "neutral"},
        tutors: {value: "Varies ⚠️", status: "neutral"},
        apps: {value: "Limited ⚠️", status: "neutral"},
        highlight: true
    },
    {
        name: "Accent & pronunciation focus",
        mockready: "Advanced ✓",
        mockreaddyNote: "With AI voice analysis",
        traditional: {value: "Moderate ⚠️", status: "neutral"},
        tutors: {value: "Good ✓", status: "positive"},
        apps: {value: "Basic ✕", status: "negative"},
        highlight: true
    },
    {
        name: "Personalized learning path",
        mockready: "Yes ✓",
        traditional: {value: "No ✕", status: "negative"},
        tutors: {value: "Yes ✓", status: "positive"},
        apps: {value: "Limited ⚠️", status: "neutral"},
        highlight: false
    }
];

// Function to get the appropriate CSS class based on status
const getStatusClass = (status) => {
    switch (status) {
        case 'positive':
            return 'text-green-600 font-medium';
        case 'negative':
            return 'text-red-600';
        case 'neutral':
            return 'text-yellow-600';
        default:
            return '';
    }
};
</script>

<template>
    <div>
        <Head>
            <title>{{ metaData.title }}</title>
            <meta name="description" :content="metaData.description">
            <meta property="og:title" :content="`${metaData.title} | MockReady`">
            <meta property="og:description" :content="metaData.description">
            <link rel="canonical" :href="metaData.canonical"/>
        </Head>
        <GeneralLayout>


            <div class="bg-[#F0FFF3] py-8 sm:py-12">
                <section class="max-w-screen-xl mx-auto py-6 sm:py-8 px-4 sm:px-5 lg:px-6">
                    <div class="flex flex-col lg:flex-row items-center justify-center gap-8">
                        <div class="w-full text-center sm:text-left">
                            <h1 class="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 text-primary-600">
                                Speak confidently when it matters
                            </h1>

                            <p class="text-lg sm:text-xl text-gray-700 mb-6">
                                Practise job interviews, workplace conversations and IELTS speaking with an AI coach.
                                Get a scored report with corrections in about 5 minutes.
                            </p>

                            <!-- Segment tabs: switch the copy below and where the CTA leads -->
                            <div class="mb-5 -mx-4 px-4 overflow-x-auto sm:mx-0 sm:px-0">
                                <div role="tablist" aria-label="What are you preparing for?"
                                     class="inline-flex bg-white rounded-full p-1 shadow-sm whitespace-nowrap">
                                    <button v-for="segment in segments"
                                            :key="segment.key"
                                            type="button"
                                            role="tab"
                                            :id="`segment-tab-${segment.key}`"
                                            :aria-selected="userSegment === segment.key"
                                            aria-controls="segment-panel"
                                            @click="setUserSegment(segment.key)"
                                            :class="[
                                                'px-4 sm:px-5 py-2 rounded-full text-sm font-medium transition',
                                                userSegment === segment.key
                                                    ? 'bg-blue-600 text-white'
                                                    : 'text-gray-600 hover:text-gray-900'
                                            ]">
                                        {{ segment.tab }}
                                    </button>
                                </div>
                            </div>

                            <div id="segment-panel" role="tabpanel" :aria-labelledby="`segment-tab-${activeSegment.key}`"
                                 class="text-left mb-6">
                                <p class="text-base sm:text-lg font-medium text-gray-900 mb-3">{{ activeSegment.intro }}</p>
                                <ul class="space-y-2 mb-4">
                                    <li v-for="bullet in activeSegment.bullets" :key="bullet"
                                        class="flex items-start gap-2 text-gray-700">
                                        <svg class="mt-0.5 h-5 w-5 shrink-0 text-primary-600" fill="none" stroke="currentColor"
                                             viewBox="0 0 24 24" aria-hidden="true">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                                        </svg>
                                        <span>{{ bullet }}</span>
                                    </li>
                                </ul>
                                <div class="rounded-lg bg-white/70 border border-gray-200 p-4">
                                    <p class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Example questions</p>
                                    <ul class="space-y-1 text-sm text-gray-700">
                                        <li v-for="question in activeSegment.examples" :key="question">“{{ question }}”</li>
                                    </ul>
                                </div>
                            </div>

                            <div class="rounded-md">
                                <template v-if="isAuthenticated">
                                    <Link :href="route('ai_peer.start_screen', {session_mode: 'quick', track: activeSegment.key})"
                                          @click.capture="onHeroStartClick"
                                          class="w-full max-w-md mx-auto sm:mx-0 inline-flex items-center justify-center gap-2 rounded-md bg-primary-600 px-5 py-4 text-base sm:text-lg font-semibold text-white shadow-sm transition hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                                        {{ activeSegment.ctaLabel }}
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                             stroke-width="1.5" stroke="currentColor" class="w-6 h-6" aria-hidden="true">
                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                  d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3"/>
                                        </svg>
                                    </Link>
                                    <p class="mt-2 text-sm text-gray-700 text-center sm:text-left">5-minute session · scored report with corrections</p>
                                </template>
                                <template v-else>
                                    <div class="flex flex-col gap-3 max-w-md mx-auto sm:mx-0">
                                        <GoogleAuthButton location="home_hero" size="lg" :track="activeSegment.key"/>
                                        <Link :href="route('register', {intent: 'practice', track: activeSegment.key})"
                                              @click.capture="onHeroEmailClick"
                                              class="w-full inline-flex items-center justify-center rounded-md bg-primary-600 px-5 py-4 text-base sm:text-lg font-semibold text-white shadow-sm transition hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                                            Sign up with email
                                        </Link>
                                        <p class="text-sm text-gray-700 text-center sm:text-left">
                                            <span class="font-semibold">Free:</span> 5 AI speaking sessions with scored feedback. No card needed.
                                        </p>
                                    </div>
                                </template>
                            </div>
                        </div>
                        <div class="w-full max-w-xs mx-auto sm:max-w-md lg:max-w-none">
                            <img src="/assets/images/homepage/homepage-hero.png"
                                 alt="Practising English speaking with an AI coach"
                                 class="h-auto">
                        </div>
                    </div>
                </section>
            </div>

            <!-- Social proof moved out of the hero until the numbers are verified -->
            <section class="max-w-screen-xl mx-auto pt-8 px-4 sm:px-6 lg:px-8">
                <div class="flex flex-wrap items-center justify-center gap-8">
                    <!-- TODO(owner): verify claim ("25,000+ Active Users") -->
                    <div class="text-center">
                        <div class="text-3xl font-bold text-blue-600">25,000+</div>
                        <div class="text-sm text-gray-600">Active Users</div>
                    </div>
                    <!-- TODO(owner): verify claim ("98% Satisfaction Rate") -->
                    <div class="text-center">
                        <div class="text-3xl font-bold text-blue-600">98%</div>
                        <div class="text-sm text-gray-600">Satisfaction Rate</div>
                    </div>
                    <!-- TODO(owner): verify claim (Trustpilot rating badge matches a real Trustpilot profile) -->
                    <img src="/assets/images/homepage/Trustpilot Micro.svg" alt="Trustpilot Rating">
                </div>
            </section>

            <section class="max-w-screen-xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
                <!-- TODO(owner): verify claim: Cambridge University / British Council / IELTS Official / Deloitte logos imply endorsement (trademark risk) -->
                <p class="text-center text-gray-600 mb-4">Trusted by international learners from trusted businesses</p>
                <div class="flex flex-wrap items-center justify-between">
                    <img v-for="company in companies" :key="company.name"
                         :src="'/assets/images/homepage/trustees/' + company.image"
                         :alt="company.name + ' Logo'"
                         class="my-2 h-6 sm:h-9 md:h-12 lg:h-16 w-auto  hover:grayscale-0 transition-all duration-300"/>
                </div>
            </section>


            <section>
                <div class="max-w-screen-xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
                    <h2 class="text-center font-semibold text-2xl sm:text-3xl md:text-4xl">
                        How it works
                    </h2>
                    <div class="text-gray-700 text-center py-6 sm:py-8 px-0 sm:px-6 md:px-12">
                        Rehearse the real conversation, then see exactly what to fix. Practise with the AI coach any
                        time, or schedule a session with another learner.
                    </div>

                    <div>
                        <ul class="flex flex-wrap justify-center -mb-px text-sm font-medium text-center text-gray-500 dark:text-gray-400">
                            <li class="me-2" v-for="(tab, index) in howItWorksTabs" :key="index">

                                <a href="#"
                                   @click.prevent="activeTab = tab.name"
                                   class="inline-flex items-center justify-center p-4 rounded-t-lg group"
                                   :class="{
                          'border-b-2 border-transparent hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300': activeTab !== tab.name,
                          'text-primary-600 border-b-2 border-primary-600 dark:text-primary-500 dark:border-primary-500': activeTab === tab.name
                        }"
                                >
                                    <svg v-html="tab.icon" class="w-6 h-6 me-2"
                                         :class="{'text-gray-400 group-hover:text-gray-500 dark:text-gray-500 dark:group-hover:text-gray-300': activeTab !== tab.name, 'text-blue-600 dark:text-blue-500': activeTab === tab.name}"
                                         aria-hidden="true"></svg>
                                    {{ tab.label }}
                                </a>
                            </li>
                        </ul>

                        <div class="p-4">
                            <div v-if="activeTab === 'aipeer'">
                                <div class="flex flex-wrap items-stretch gap-x-3 gap-y-8 justify-center">
                                    <div
                                        class="rounded-md flex flex-col items-center justify-center bg-gray-100 max-w-sm">
                                        <div class="w-full h-48 sm:h-64 object-cover">
                                            <img src="/assets/images/homepage/how-it-works-create-account.png"
                                                 alt="Choose what you are preparing for"
                                                 class="object-cover w-full h-full">
                                        </div>
                                        <div class="text-center p-4 sm:p-8">
                                            <h3 class="text-xl sm:text-2xl font-semibold text-gray-800">1. Pick what you're preparing for</h3>
                                            <p>A job interview, everyday work conversations, or the IELTS speaking test.
                                                Your free account is ready in under a minute.</p>
                                            <Link :href="isAuthenticated ? route('dashboard') : route('register', {intent: 'practice'})">
                                                <button
                                                    class="my-3 block w-full bg-primary-800 text-white font-bold py-2 px-4 rounded hover:bg-green-600 transition duration-200">
                                                    {{ isAuthenticated ? 'Go to your dashboard' : 'Start for free' }}
                                                </button>
                                            </Link>
                                        </div>
                                    </div>
                                    <div
                                        class="rounded-md flex flex-col items-center justify-center bg-gray-100 max-w-sm">
                                        <div class="w-full h-48 sm:h-64 object-cover">
                                            <img src="/assets/images/homepage/how-it-works-solo-practice.png"
                                                 alt="Answering questions out loud with an AI speaking coach"
                                                 class="object-cover w-full h-full">
                                        </div>
                                        <div class="text-center p-4 sm:p-8">
                                            <h3 class="text-xl sm:text-2xl font-semibold text-gray-800">2. Answer out loud</h3>
                                            <p>The AI coach reads each question aloud and you answer with your
                                                microphone, just like the real conversation. It takes about 5 minutes.</p>
                                        </div>
                                    </div>
                                    <div
                                        class="rounded-md flex flex-col items-center justify-center bg-gray-100 max-w-sm">
                                        <div class="w-full h-48 sm:h-64 object-cover">
                                            <img src="/assets/images/homepage/how-it-works-check-progress.png"
                                                 alt="Scored speaking report with corrections"
                                                 class="object-cover w-full h-full">
                                        </div>
                                        <div class="text-center p-4 sm:p-8">
                                            <h3 class="text-xl sm:text-2xl font-semibold text-gray-800">3. Get your scored report</h3>
                                            <p>A score for fluency, vocabulary, grammar and pronunciation, plus your
                                                mistakes with corrections, so you know what to fix before the real thing.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div v-if="activeTab === 'paired'">
                                <div class="flex flex-wrap items-stretch gap-x-3 gap-y-8 justify-center">
                                    <div
                                        class="rounded-md flex flex-col items-center justify-center bg-gray-100 max-w-sm">
                                        <div class="w-full h-48 sm:h-64 object-cover">
                                            <img
                                                src="/assets/images/homepage/how-it-works-paired-practice-create-session.png"
                                                alt="Schedule a practice session"
                                                class="object-cover w-full h-full">
                                        </div>
                                        <div class="text-center p-4 sm:p-8">
                                            <h3 class="text-xl sm:text-2xl font-semibold text-gray-800">Schedule a session</h3>
                                            <p>Go to your dashboard and pick a time to practise with another learner.</p>
                                            <Link :href="isAuthenticated ? route('dashboard') : route('register')">
                                                <button
                                                    class="my-3 block w-full bg-primary-800 text-white font-bold py-2 px-4 rounded hover:bg-green-600 transition duration-200">
                                                    Get Started
                                                </button>
                                            </Link>
                                        </div>
                                    </div>
                                    <div
                                        class="rounded-md flex flex-col items-center justify-center bg-gray-100 max-w-sm">
                                        <div class="w-full h-48 sm:h-64 object-cover">
                                            <img src="/assets/images/homepage/how-it-works-paired-practice-session.png"
                                                 alt="Practising on a video call with a partner"
                                                 class="object-cover w-full h-full">
                                        </div>
                                        <div class="text-center p-4 sm:p-8">
                                            <h3 class="text-xl sm:text-2xl font-semibold text-gray-800">Practise with a partner</h3>
                                            <p>Join the video call and take turns asking and answering questions.</p>
                                        </div>
                                    </div>
                                    <div
                                        class="rounded-md flex flex-col items-center justify-center bg-gray-100 max-w-sm">
                                        <div class="w-full h-48 sm:h-64 object-cover">
                                            <img src="/assets/images/homepage/how-it-works-paired-practice-feedback.png"
                                                 alt="Giving and receiving feedback"
                                                 class="object-cover w-full h-full">
                                        </div>
                                        <div class="text-center p-4 sm:p-8">
                                            <h3 class="text-xl sm:text-2xl font-semibold text-gray-800">Give and receive feedback</h3>
                                            <p>Tell each other what went well and what to work on.</p>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </section>


            <section class="bg-[#F0FFF3] py-12">
                <div class="max-w-screen-xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
                    <h2 class="text-center font-semibold text-2xl sm:text-3xl md:text-4xl">
                        Why rehearsing out loud works
                    </h2>
                    <div class="text-gray-700 text-center py-5 px-0 sm:px-12">
                        Reading tips won't make you fluent under pressure. Saying the answer out loud, then fixing what
                        went wrong, will.
                    </div>
                    <div class="flex flex-wrap -mx-4">
                        <div class="w-full md:w-1/3 px-4 py-4">
                            <img src="/assets/images/homepage/features-left.png" alt="Practise any time"
                                 class="w-full h-auto">
                            <div class="text-center mt-4">
                                <h3 class="font-bold text-xl">Practise any time</h3>
                                <p>No tutor to book. Five minutes before work or the night before your interview.</p>
                            </div>
                        </div>
                        <div class="w-full md:w-1/3 px-4 py-4">
                            <div class="flex align-center flex-col">
                                <img src="/assets/images/homepage/features-center.png" alt="Realistic questions read aloud"
                                     class="w-full h-auto">
                                <img src="/assets/images/homepage/features-center-footer.svg"
                                     alt="" aria-hidden="true" class="w-full h-auto py-8">
                                <div class="text-center mt-4">
                                    <h3 class="font-bold text-xl">Realistic questions</h3>
                                    <p>Interview, workplace and IELTS-style questions, read aloud like the real thing.</p>
                                </div>
                            </div>
                        </div>
                        <div class="w-full md:w-1/3 px-4 py-4">
                            <img src="/assets/images/homepage/features-right.png" alt="Scored report with corrections"
                                 class="w-full h-auto">
                            <div class="text-center mt-4">
                                <h3 class="font-bold text-xl">A report you can act on</h3>
                                <p>Scores on four criteria, your mistakes corrected, and your progress over time.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- IELTS: still a core track, and the entry point for our IELTS content pages -->
            <section class="max-w-screen-xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
                <div class="grid gap-8 lg:grid-cols-2 lg:items-center">
                    <div>
                        <p class="text-sm font-semibold uppercase tracking-wide text-primary-700 mb-2">IELTS Speaking</p>
                        <h2 class="font-semibold text-2xl sm:text-3xl md:text-4xl mb-4">Preparing for the IELTS speaking test?</h2>
                        <p class="text-gray-700 mb-4">
                            Take a 5-minute Part 1 test or a full 3-part mock with an AI examiner, and get an estimated
                            band score on all four IELTS criteria with your mistakes corrected.
                        </p>
                        <Link :href="isAuthenticated
                                    ? route('ai_peer.start_screen', {session_mode: 'quick', track: 'ielts'})
                                    : route('register', {intent: 'practice', track: 'ielts'})"
                              @click.capture="track('cta_clicked', {location: 'home_ielts_section', label: 'start_ielts'})"
                              class="inline-flex items-center justify-center rounded-md bg-primary-600 px-5 py-3 font-semibold text-white shadow-sm transition hover:bg-primary-700 w-full sm:w-auto">
                            Practise IELTS speaking
                        </Link>
                    </div>
                    <div class="bg-gray-50 rounded-md p-5 sm:p-9">
                        <!-- TODO(owner): verify testimonial is from a real user (name, score 6.0 → 7.5) -->
                        <div class="py-4 text-lg sm:text-xl">
                            "I raised my IELTS speaking score from 6.0 to 7.5 in just 4 weeks using MockReady! The AI
                            feedback pointed out pronunciation issues that even my teacher missed, and the peer practice
                            sessions gave me real conversation experience. It's worth every penny - I finally got the
                            score I needed for my university application."
                        </div>
                        <div class="flex items-center">
                            <div>
                                <img src="/assets/images/homepage/small-girl-avatar.png" alt="Harpreet Kaur"
                                     class="w-12 h-12 rounded-full">
                            </div>
                            <div class="px-5">
                                <div class="text-lg font-semibold text-gray-800">Harpreet Kaur</div>
                                <div class="text-sm text-gray-600">IELTS Score: 6.0 → 7.5 | Amritsar, India</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="mt-8">
                    <h3 class="text-lg font-semibold text-gray-900 mb-3">Free IELTS speaking resources</h3>
                    <ul class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        <li>
                            <Link :href="route('content_hubs.cue_cards')"
                                  class="block rounded-lg border border-gray-200 p-4 hover:border-primary-400 hover:bg-primary-50 transition">
                                IELTS speaking cue cards
                            </Link>
                        </li>
                        <li>
                            <Link :href="route('content_hubs.question_topics')"
                                  class="block rounded-lg border border-gray-200 p-4 hover:border-primary-400 hover:bg-primary-50 transition">
                                IELTS Part 1 questions
                            </Link>
                        </li>
                        <li>
                            <Link :href="route('content_hubs.sample_speaking_exams')"
                                  class="block rounded-lg border border-gray-200 p-4 hover:border-primary-400 hover:bg-primary-50 transition">
                                IELTS speaking test samples
                            </Link>
                        </li>
                        <li>
                            <Link :href="route('content_hubs.speaking_exam_material')"
                                  class="block rounded-lg border border-gray-200 p-4 hover:border-primary-400 hover:bg-primary-50 transition">
                                IELTS speaking material
                            </Link>
                        </li>
                    </ul>
                </div>
            </section>

            <section v-if="config && config.showPrices">
                <div class="max-w-screen-xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
                    <div class="text-center font-semibold text-2xl sm:text-3xl md:text-4xl">
                        Choose your plan
                    </div>
                    <div class="text-gray-700 text-center py-5 px-2 sm:px-12">
                        All plans include core features. Choose the option that fits your goals and schedule.
                    </div>

                    <div class="flex flex-wrap -mx-4">
                        <div v-for="plan in plans"
                             :key="plan.planId"
                             class="w-full md:w-1/3 px-4 py-4">
                            <div :class="[
                    'bg-gray-50 p-6 rounded-lg shadow-lg relative',
                    plan.featured ? 'border-4 border-red-500' : ''
                ]">
                    <span v-if="plan.featured"
                          class="absolute top-0 right-0 bg-red-500 text-white py-1 px-3 text-xs font-bold rounded-bl-lg">
                        Most Popular
                    </span>

                                <h2 class="text-xl font-bold text-gray-800 mb-2">{{ plan.name }}</h2>
                                <p class="text-gray-600 mb-5">{{ plan.description }}</p>

                                <p v-if="plan.originalPrice > plan.price"
                                   class="line-through text-gray-400 text-2xl mb-1">
                                    ${{ formatPrice(plan.originalPrice) }}
                                </p>
                                <p class="text-4xl font-bold text-gray-900 mb-5">
                                    ${{ formatPrice(plan.price) }}
                                    <span class="text-lg font-normal text-gray-600">
                            {{ getPeriodText(plan.interval, plan.intervalCount) }}
                        </span>
                                </p>

                                <!-- Results-focused benefit -->
                                <p class="text-green-600 font-medium mb-4">
                                    {{
                                        plan.price === 0 ? 'Get started with basic practice' :
                                            plan.featured ? 'Ideal for regular practice' :
                                                'Best value for long-term improvement'
                                    }}
                                </p>

                                <!-- Action Button -->
                                <Link v-if="!isAuthenticated"
                                      :href="route('register')"
                                      class="block w-full bg-primary-600 text-white font-bold py-2 px-4 rounded hover:bg-green-600 transition duration-200">
                                    {{ plan.price === 0 ? 'Start Free Plan' : 'Start 7-Day Free Trial' }}
                                </Link>
                                <a v-else-if="plan.stripePriceId"
                                   :href="route('subscription.checkout', { plan: plan.planId, trial_days: 7 })"
                                   class="block w-full bg-primary-600 text-white font-bold py-2 px-4 rounded hover:bg-green-600 transition duration-200">
                                    {{ plan.price === 0 ? 'Current Plan' : 'Upgrade Now' }}
                                </a>

                                <!-- Features List -->
                                <div class="mt-6">
                                    <div v-for="(value, key) in plan.features"
                                         :key="key"
                                         class="flex items-center mt-3 first:mt-0">
                            <span class="text-green-500 mr-2">
                                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                          d="M5 13l4 4L19 7"/>
                                </svg>
                            </span>
                                        <span class="text-gray-700 text-sm">
                                {{ typeof value === 'boolean' ? key : value }}
                            </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Money-back guarantee -->
                    <div class="mt-8 text-center max-w-md mx-auto bg-green-50 p-4 rounded-lg border border-green-200">
                        <p class="text-green-700 font-medium">🔒 30-day money-back guarantee if you're not satisfied</p>
                    </div>
                </div>
            </section>
            <section class="bg-gray-50 py-8">
                <section class="max-w-screen-xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
                    <h2 class="text-center font-semibold text-2xl sm:text-3xl md:text-4xl mb-4">
                        How MockReady compares
                    </h2>
                    <div class="text-gray-700 text-center py-5 px-0 sm:px-12 mb-8">
                        Rehearse the real conversation and get a scored report, whenever you have 5 minutes.
                    </div>
                    <div class="overflow-x-auto">
                        <table class="min-w-full text-sm sm:text-base divide-y divide-gray-200 bg-white">
                            <!-- Header -->
                            <thead>
                            <tr class="mx-auto">
                                <th class="px-4 py-8 font-bold text-left text-gray-700 bg-gray-50">Features</th>
                                <th class="px-4 py-8 bg-primary-600 text-white font-bold text-center border border-r-gray-200">
                                    <div class="flex flex-col-reverse items-center">
                                        <div class="text-lg">MockReady</div>
                                        <svg class="w-8 h-8 text-gray-100 mb-2" aria-hidden="true"
                                             xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                             fill="currentColor" viewBox="0 0 24 24">
                                            <path fill-rule="evenodd"
                                                  d="M11 4.717c-2.286-.58-4.16-.756-7.045-.71A1.99 1.99 0 0 0 2 6v11c0 1.133.934 2.022 2.044 2.007 2.759-.038 4.5.16 6.956.791V4.717Zm2 15.081c2.456-.631 4.198-.829 6.956-.791A2.013 2.013 0 0 0 22 16.999V6a1.99 1.99 0 0 0-1.955-1.993c-2.885-.046-4.76.13-7.045.71v15.081Z"
                                                  clip-rule="evenodd"/>
                                        </svg>
                                    </div>
                                </th>
                                <th class="px-4 py-8 font-bold text-center text-gray-700 bg-gray-50 border border-r-gray-200">
                                    <div class="flex flex-col-reverse items-center">
                                        <div class="text-lg">Traditional Classes</div>
                                        <svg class="w-8 h-8 text-gray-500 mb-2" aria-hidden="true"
                                             xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                             fill="currentColor" viewBox="0 0 24 24">
                                            <path fill-rule="evenodd"
                                                  d="M11 4.717c-2.286-.58-4.16-.756-7.045-.71A1.99 1.99 0 0 0 2 6v11c0 1.133.934 2.022 2.044 2.007 2.759-.038 4.5.16 6.956.791V4.717Zm2 15.081c2.456-.631 4.198-.829 6.956-.791A2.013 2.013 0 0 0 22 16.999V6a1.99 1.99 0 0 0-1.955-1.993c-2.885-.046-4.76.13-7.045.71v15.081Z"
                                                  clip-rule="evenodd"/>
                                        </svg>
                                    </div>
                                </th>
                                <th class="px-4 py-8 font-bold text-center text-gray-700 bg-gray-50 border border-r-gray-200">
                                    <div class="flex flex-col-reverse items-center">
                                        <div class="text-lg">Private Tutors</div>
                                        <svg class="w-8 h-8 text-gray-500 mb-2" aria-hidden="true"
                                             xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                             fill="currentColor" viewBox="0 0 24 24">
                                            <path fill-rule="evenodd"
                                                  d="M11 4.717c-2.286-.58-4.16-.756-7.045-.71A1.99 1.99 0 0 0 2 6v11c0 1.133.934 2.022 2.044 2.007 2.759-.038 4.5.16 6.956.791V4.717Zm2 15.081c2.456-.631 4.198-.829 6.956-.791A2.013 2.013 0 0 0 22 16.999V6a1.99 1.99 0 0 0-1.955-1.993c-2.885-.046-4.76.13-7.045.71v15.081Z"
                                                  clip-rule="evenodd"/>
                                        </svg>
                                    </div>
                                </th>
                                <th class="px-4 py-8 font-bold text-center text-gray-700 bg-gray-50 border border-r-gray-200">
                                    <div class="flex flex-col-reverse items-center">
                                        <div class="text-lg">Language Apps</div>
                                        <svg class="w-8 h-8 text-gray-500 mb-2" aria-hidden="true"
                                             xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                             fill="currentColor" viewBox="0 0 24 24">
                                            <path fill-rule="evenodd"
                                                  d="M11 4.717c-2.286-.58-4.16-.756-7.045-.71A1.99 1.99 0 0 0 2 6v11c0 1.133.934 2.022 2.044 2.007 2.759-.038 4.5.16 6.956.791V4.717Zm2 15.081c2.456-.631 4.198-.829 6.956-.791A2.013 2.013 0 0 0 22 16.999V6a1.99 1.99 0 0 0-1.955-1.993c-2.885-.046-4.76.13-7.045.71v15.081Z"
                                                  clip-rule="evenodd"/>
                                        </svg>
                                    </div>
                                </th>
                            </tr>
                            </thead>
                            <!-- Body -->
                            <tbody class="divide-y divide-gray-200">
                            <!-- Use v-for to dynamically generate comparison rows -->
                            <tr v-for="(feature, index) in comparisonFeatures" :key="index"
                                :class="{'bg-blue-50': feature.highlight}">
                                <td class="px-4 py-6 font-medium text-gray-900">{{ feature.name }}</td>
                                <td class="px-4 py-6 text-center text-white bg-primary-500">
                                    <div>{{ feature.mockready }}</div>
                                    <div v-if="feature.mockreaddyNote" class="text-xs mt-1">{{
                                            feature.mockreaddyNote
                                        }}
                                    </div>
                                </td>
                                <td class="px-4 py-6 text-center" :class="getStatusClass(feature.traditional.status)">
                                    {{ feature.traditional.value }}
                                </td>
                                <td class="px-4 py-6 text-center" :class="getStatusClass(feature.tutors.status)">
                                    {{ feature.tutors.value }}
                                </td>
                                <td class="px-4 py-6 text-center" :class="getStatusClass(feature.apps.status)">
                                    {{ feature.apps.value }}
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                    <!-- TODO(owner): verify claims ("94%", "1.2 points", "85% within 30 days") -->
                    <div class="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div class="bg-white p-4 rounded-lg shadow">
                            <div class="font-bold text-2xl text-green-600 mb-2">94%</div>
                            <p class="text-gray-700">of our users report feeling more confident in English conversations
                                after just 2 weeks</p>
                        </div>
                        <div class="bg-white p-4 rounded-lg shadow">
                            <div class="font-bold text-2xl text-green-600 mb-2">1.2 points</div>
                            <p class="text-gray-700">average IELTS speaking score improvement for users who practice 3x
                                weekly</p>
                        </div>
                        <div class="bg-white p-4 rounded-lg shadow">
                            <div class="font-bold text-2xl text-green-600 mb-2">85%</div>
                            <p class="text-gray-700">of users achieve their target IELTS score within 30 days of regular
                                practice</p>
                        </div>
                    </div>

                </section>

            </section>
            <section title="Frequently Asked Questions" class="max-w-screen-xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
                <h2 class="text-center font-semibold text-2xl sm:text-3xl md:text-4xl mb-6">
                    Frequently Asked Questions
                </h2>
                <div class="text-gray-700 text-center mb-8 px-0 sm:px-12">
                    How MockReady helps you get ready for interviews, work conversations and the IELTS speaking test
                </div>

                <div v-for="(questions, category) in faqItems" :key="category">
                    <h3 class="my-6 text-xl font-extrabold text-gray-900 dark:text-white">{{ category }}</h3>
                    <fwb-accordion flush>
                        <fwb-accordion-panel v-for="item in questions" :key="item.question">
                            <fwb-accordion-header>{{ item.question }}</fwb-accordion-header>
                            <fwb-accordion-content>
                                <div>
                                    <p class="mb-2 text-gray-500 dark:text-gray-400">
                                        {{ item.answer }}
                                    </p>
                                </div>
                            </fwb-accordion-content>
                        </fwb-accordion-panel>
                    </fwb-accordion>
                </div>
            </section>

        </GeneralLayout>

    </div>
</template>

<style>
</style>
